import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Target, Crown, Shield, Flame } from 'lucide-react';
import StreakDisplay from './StreakDisplay';
import ClinicalModeIndicator from './ClinicalModeIndicator';
import { PROFILE_DEFINITIONS } from '../utils/themeManager';
import { getLocalDateString } from '../utils/dateUtils';
import { api } from '../context/AppContext';

export const DashboardHeader = React.memo(function DashboardHeader({
  user, language, t, selectedDate, setSelectedDate,
  showDatePicker, setShowDatePicker, incompleteDays, clinicalMode
}) {
  const navigate = useNavigate();
  // ─── Iter. 411.cc — Live profile grade (Recrue → Legende, etc.) ───
  // Silent fetch: fails soft to just the static "Profil Pilote" label.
  const [gradeData, setGradeData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!user?.active_profile) return;
    api.get('/gamification/profile-grade/current')
      .then((res) => { if (!cancelled) setGradeData(res.data); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [user?.active_profile]);

  return (
    <div className="mb-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-foreground mb-2">
            {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground flex items-center gap-2">
            <span>
              {selectedDate
                ? new Date(selectedDate + 'T12:00:00').toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                : new Date().toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
              }
            </span>
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest transition-all active:scale-95 flex items-center gap-1.5"
              data-testid="history-date-picker-btn"
              title={language === 'fr' ? 'Editer une journée passée (jusqu\'à 30 jours)' : 'Edit a past day (up to 30 days)'}
              style={{
                background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(251,146,60,0.22) 100%)',
                border: '1px solid rgba(251,146,60,0.55)',
                boxShadow: '0 0 14px rgba(251,146,60,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
                backdropFilter: 'blur(6px)',
                color: '#fed7aa',
                textShadow: '0 0 6px rgba(251,146,60,0.65)',
              }}
            >
              <CalendarDays className="w-3.5 h-3.5" style={{ filter: 'drop-shadow(0 0 5px rgba(251,146,60,0.9))' }} />
              {language === 'fr' ? 'Rattraper' : 'Catch up'}
            </button>
            {selectedDate && (
              <button
                onClick={() => setSelectedDate(null)}
                className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full transition-all active:scale-95"
                data-testid="history-return-today"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(251,146,60,0.28) 100%)',
                  border: '1px solid rgba(251,146,60,0.55)',
                  boxShadow: '0 0 12px rgba(251,146,60,0.45)',
                  backdropFilter: 'blur(6px)',
                  color: '#fed7aa',
                  textShadow: '0 0 5px rgba(251,146,60,0.65)',
                }}
              >
                {language === 'fr' ? "Retour aujourd'hui" : 'Back to today'}
              </button>
            )}
          </p>

          {/* Historical Date Picker — Phase 1 (2026-04-29): extended to 30j */}
          {showDatePicker && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 p-3 bg-card border border-border rounded-xl shadow-xl"
              data-testid="history-date-picker"
            >
              <p className="text-xs text-muted-foreground mb-2 font-medium uppercase tracking-wide">
                {language === 'fr' ? 'Choisis la date à éditer' : 'Pick a date to edit'}
              </p>
              {/* Quick-pick chips: last 7 days (most common) */}
              <div className="flex flex-wrap gap-2 mb-3">
                {Array.from({ length: 7 }, (_, i) => {
                  const d = new Date();
                  d.setDate(d.getDate() - (i + 1));
                  const dateStr = getLocalDateString(d);
                  const dayName = d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short' });
                  const dayNum = d.getDate();
                  const isIncomplete = incompleteDays.some(x => x.date === dateStr);
                  const isSelected = selectedDate === dateStr;
                  return (
                    <button
                      key={dateStr}
                      onClick={() => { setSelectedDate(dateStr); setShowDatePicker(false); }}
                      className={`flex flex-col items-center p-2 rounded-lg min-w-[48px] transition-all border ${
                        isSelected
                          ? 'bg-orange-500 text-white border-orange-500 shadow-lg shadow-orange-500/30'
                          : isIncomplete
                            ? 'bg-red-500/10 border-red-500/30 text-red-400 hover:border-red-500'
                            : 'bg-secondary border-transparent hover:border-muted-foreground/30 text-muted-foreground'
                      }`}
                      data-testid={`history-day-${dateStr}`}
                    >
                      <span className="text-[10px] uppercase font-bold">{dayName}</span>
                      <span className="text-lg font-black">{dayNum}</span>
                      {isIncomplete && !isSelected && <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-0.5" />}
                    </button>
                  );
                })}
              </div>
              {/* Native date input — covers the full 30-day window (J-1 → J-30) */}
              <div className="border-t border-border/50 pt-3">
                <p className="text-[10px] text-muted-foreground mb-1.5 uppercase tracking-widest font-bold">
                  {language === 'fr' ? 'Plus loin (jusqu\'à 30 jours)' : 'Further back (up to 30 days)'}
                </p>
                <input
                  type="date"
                  value={selectedDate || ''}
                  min={(() => { const d = new Date(); d.setDate(d.getDate() - 30); return getLocalDateString(d); })()}
                  max={(() => { const d = new Date(); d.setDate(d.getDate() - 1); return getLocalDateString(d); })()}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v) { setSelectedDate(v); setShowDatePicker(false); }
                  }}
                  className="w-full h-9 px-2 text-sm rounded-md bg-secondary border border-border text-foreground"
                  data-testid="history-date-input"
                />
              </div>
            </motion.div>
          )}

          {/* Historical Mode Banner */}
          {selectedDate && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-xl flex items-center gap-3"
              data-testid="history-mode-banner"
            >
              <CalendarDays className="w-5 h-5 text-orange-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-orange-400">
                  {language === 'fr' ? 'MODE EDITION HISTORIQUE' : 'HISTORY EDIT MODE'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {language === 'fr'
                    ? 'Tes logs seront enregistres pour cette date. Rattrape ton Extra Mile!'
                    : 'Your logs will be saved for this date. Catch up on your Extra Mile!'}
                </p>
              </div>
            </motion.div>
          )}

          {/* Clinical Mode Indicator */}
          {clinicalMode?.enabled && (
            <div className="mt-2">
              <ClinicalModeIndicator clinicalMode={clinicalMode} variant="compact" />
            </div>
          )}

          {/* Cultural/Religious Mode Indicator — GLOW aesthetic (Iter. 411.v) */}
          {user?.cultural_mode && user.cultural_mode !== 'standard' && (
            <div className="mt-2" data-testid="cultural-mode-banner">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(245,158,11,0.22) 100%)',
                  border: '1px solid rgba(245,158,11,0.55)',
                  boxShadow: '0 0 14px rgba(245,158,11,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                <span
                  className="text-base"
                  style={{ filter: 'drop-shadow(0 0 5px rgba(245,158,11,0.9))' }}
                >
                  {user.cultural_mode === 'ramadan' ? '\u{1F319}' :
                   user.cultural_mode === 'halal' ? '\u{262A}' :
                   user.cultural_mode === 'kasher' ? '\u{2721}' :
                   user.cultural_mode === 'careme' ? '\u{271D}' :
                   user.cultural_mode === 'hindou' ? '\u{1F549}' : '\u{1F30D}'}
                </span>
                <span
                  className="text-[11px] font-black uppercase tracking-widest text-amber-100"
                  style={{ textShadow: '0 0 6px rgba(245,158,11,0.65)' }}
                >
                  {language === 'fr'
                    ? `Mode ${user.cultural_mode === 'ramadan' ? 'Ramadan' : user.cultural_mode === 'halal' ? 'Halal' : user.cultural_mode === 'kasher' ? 'Kasher' : user.cultural_mode === 'careme' ? 'Careme' : user.cultural_mode === 'hindou' ? 'Hindou' : user.cultural_mode} actif`
                    : `${user.cultural_mode} mode active`}
                </span>
              </div>
            </div>
          )}

          {/* Active Profile Badge */}
          {/* Active Profile Badge — GLOW aesthetic + live grade (Iter. 411.cc) */}
          {user?.active_profile && PROFILE_DEFINITIONS[user.active_profile] && (() => {
            const profileDef = PROFILE_DEFINITIONS[user.active_profile];
            const icons = { Target, Crown, Shield, Flame };
            const ProfileIcon = icons[profileDef.icon] || Target;
            // Convert `#RRGGBB` to `r,g,b` so rgba() shadows read cleanly.
            const hexToRgb = (hex) => {
              const h = (hex || '#f97316').replace('#', '');
              return [
                parseInt(h.slice(0, 2), 16),
                parseInt(h.slice(2, 4), 16),
                parseInt(h.slice(4, 6), 16),
              ].join(',');
            };
            // ─── Iter. 411.cc — Prefer live grade color/label over static profile
            // once /gamification/profile-grade/current has resolved. The grade's
            // color intensifies as the user levels up (grey Recrue → gold Légende),
            // so the badge visually rewards long-term consistency.
            const grade = gradeData?.current;
            const rgb = grade?.color_rgb || hexToRgb(profileDef.colors.primary);
            const gradeLabel = grade
              ? (language === 'fr' ? grade.label_fr : grade.label_en)
              : (language === 'fr' ? `Profil ${profileDef.label}` : `${profileDef.label} Profile`);
            // Progress ring — thin outer track filled per `progress.combined`,
            // rendered as a linear-gradient wedge on the border.
            const pct = Math.max(0, Math.min(1, gradeData?.progress?.combined ?? 0));
            const nextGrade = gradeData?.next;
            const showRing = !!nextGrade && pct > 0;
            const glowIntensity = grade?.intensity || 1;
            const shadowAlpha = 0.30 + glowIntensity * 0.08;   // 0.38 → 0.70
            const glowRadius = 12 + glowIntensity * 3;         // 15 → 27px
            return (
              <div className="mt-2" data-testid="active-profile-badge">
                <button
                  type="button"
                  onClick={() => navigate('/mes-grades')}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest relative transition-transform active:scale-95 hover:brightness-110"
                  style={{
                    background: `linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(${rgb},0.22) 100%)`,
                    border: `1px solid rgba(${rgb},0.55)`,
                    boxShadow: `0 0 ${glowRadius}px rgba(${rgb},${shadowAlpha}), inset 0 1px 0 rgba(255,255,255,0.06)`,
                    backdropFilter: 'blur(6px)',
                    color: `rgba(${rgb},1)`,
                    textShadow: `0 0 6px rgba(${rgb},0.65)`,
                  }}
                  title={
                    grade && gradeData?.stats
                      ? (language === 'fr'
                          ? `${gradeData.stats.active_days} jours actifs · ${gradeData.stats.total_glory} pts totaux${nextGrade ? ` · ${Math.round(pct * 100)}% vers ${(language === 'fr' ? nextGrade.label_fr : nextGrade.label_en)}` : ''}`
                          : `${gradeData.stats.active_days} active days · ${gradeData.stats.total_glory} total pts${nextGrade ? ` · ${Math.round(pct * 100)}% to ${nextGrade.label_en}` : ''}`)
                      : undefined
                  }
                >
                  {grade?.emoji ? (
                    <span
                      className="text-sm leading-none"
                      style={{ filter: `drop-shadow(0 0 5px rgba(${rgb},0.9))` }}
                      aria-hidden="true"
                    >
                      {grade.emoji}
                    </span>
                  ) : (
                    <ProfileIcon
                      className="w-3.5 h-3.5"
                      style={{
                        color: `rgba(${rgb},1)`,
                        filter: `drop-shadow(0 0 5px rgba(${rgb},0.9))`,
                      }}
                    />
                  )}
                  <span>{gradeLabel}</span>
                  {showRing && (
                    <span
                      className="ml-1 text-[9px] font-mono font-black opacity-80"
                      style={{ textShadow: `0 0 4px rgba(${rgb},0.65)` }}
                    >
                      {Math.round(pct * 100)}%
                    </span>
                  )}
                </button>
              </div>
            );
          })()}
        </div>
        <StreakDisplay compact />
      </div>
    </div>
  );
});
